/** Installer der Windows-Desktop-App (electron-updater Feed). */
export const DESKTOP_APP_SETUP_URL =
  "https://updates.ernsthaus.de/ernsthaus-desktop/Ernsthaus-Desktop-Setup.exe";

/** Electron-Preload-API für lokale Ordner (wie `window.envAPI.openFolder` auf /auftraege). */

export interface OpenTempFilePayload {
  data: Uint8Array;
  filename: string;
}

declare global {
  interface Window {
    envAPI?: {
      openFolder?: (path: string) => Promise<string | null | undefined>;
      /** Temp-Datei schreiben und mit Standard-App öffnen (z. B. Windows Fotos). */
      openTempFile?: (payload: OpenTempFilePayload) => Promise<string>;
      /** RAW-Bytes an einen Windows-Drucker (Brother PT-P910BT). Leerer String = ok. */
      printRaw?: (payload: { data: Uint8Array; printerHint?: string }) => Promise<string>;
      isElectron?: boolean;
      openExternalLogin?: (url: string) => Promise<boolean>;
      openNewWindow?: () => Promise<boolean>;
      /**
       * Relativen Pfad in einem anderen App-Fenster öffnen.
       * Neuere Desktop-Versionen liefern "other" | "new" | false,
       * ältere true | false.
       */
      openInOtherWindow?: (path: string) => Promise<boolean | OtherWindowTarget>;
      /** SPA-Navigation aus dem Main-Prozess; Rückgabe = Unsubscribe. */
      onNavigate?: (cb: (path: string) => void) => () => void;
    };
    ehDialog?: {
      alertSync: (message: string) => void;
      confirmSync: (message: string) => boolean;
    };
    __ehNativeDialogsPatched?: boolean;
    /**
     * Anzahl offener Dialoge (ModalStackProvider). Der Electron-Main-Prozess
     * liest den Wert, um kein belegtes Fenster für Zweitfenster-Navigation
     * zu übersteuern.
     */
    __ehOpenModalCount?: number;
  }
}

/** Wo ein Pfad geöffnet wurde: freies Zweitfenster oder neues Fenster. */
export type OtherWindowTarget = "other" | "new";

export function isElectronRuntime(): boolean {
  if (typeof window === "undefined") return false;
  return window.envAPI?.isElectron === true;
}

export function openElectronNewWindow(): Promise<boolean> {
  if (typeof window.envAPI?.openNewWindow !== "function") {
    return Promise.resolve(false);
  }
  return window.envAPI.openNewWindow();
}

/**
 * Öffnet `path` (z. B. `/projekte?auftrag_id=1`) in einem anderen
 * Electron-Fenster. Belegte Fenster (offener Dialog/Editor) werden nicht
 * übersteuert – dann öffnet die Desktop-App ein neues Fenster ("new").
 * `false`: kein Zweitfenster vorhanden bzw. kein Electron.
 */
export async function openElectronInOtherWindow(
  path: string,
): Promise<OtherWindowTarget | false> {
  if (typeof window.envAPI?.openInOtherWindow !== "function") {
    return false;
  }
  const normalized = String(path || "").trim();
  if (!normalized.startsWith("/")) return false;
  try {
    const result = await window.envAPI.openInOtherWindow(normalized);
    if (result === "new") return "new";
    return result ? "other" : false;
  } catch {
    return false;
  }
}

const OTHER_WINDOW_TOAST: Record<OtherWindowTarget, string> = {
  other: "Im zweiten Fenster geöffnet — ggf. in der Taskleiste prüfen, falls es minimiert oder verdeckt ist.",
  new: "In einem neuen Fenster geöffnet.",
};

/** Hinweistext für `openElectronInOtherWindow`-Ergebnisse. */
export function otherWindowToastMessage(target: OtherWindowTarget): string {
  return OTHER_WINDOW_TOAST[target];
}

export function subscribeElectronNavigate(cb: (path: string) => void): () => void {
  if (typeof window.envAPI?.onNavigate !== "function") {
    return () => {};
  }
  return window.envAPI.onNavigate(cb);
}

export function isElectronLoginRuntime(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(
    window.envAPI?.isElectron === true &&
      typeof window.envAPI.openExternalLogin === "function",
  );
}

export function isElectronWithOpenFolder(): boolean {
  if (typeof window === "undefined") return false;
  return typeof window.envAPI?.openFolder === "function";
}

export function isElectronWithOpenTempFile(): boolean {
  if (typeof window === "undefined") return false;
  return typeof window.envAPI?.openTempFile === "function";
}

export function isElectronWithPrintRaw(): boolean {
  if (typeof window === "undefined") return false;
  return typeof window.envAPI?.printRaw === "function";
}

export async function printRawOnWindows(
  data: Uint8Array,
  printerHint = "PT-P910",
): Promise<string> {
  if (!isElectronWithPrintRaw()) {
    return "RAW-Druck ist nur in der Desktop-App verfügbar.";
  }
  return window.envAPI!.printRaw!({ data, printerHint });
}

/**
 * Datei mit der System-Standard-App öffnen (Windows: oft „Fotos“).
 * Im Browser: Download als Fallback.
 */
export async function openFileInDefaultApp(
  blob: Blob,
  filename: string,
): Promise<void> {
  const safeName = (filename || "bild.jpg").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_") || "bild.jpg";

  if (isElectronWithOpenTempFile()) {
    const data = new Uint8Array(await blob.arrayBuffer());
    const err = await window.envAPI!.openTempFile!({ data, filename: safeName });
    if (err) {
      throw new Error(err);
    }
    return;
  }

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = safeName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
